/** 可静默第三方脚本：首次失败即拦截，不重试、不上报、不向后续监听器传播 */
export const SILENT_SCRIPT_PATTERNS = [
    'clarity.ms',
    'connect.facebook.net',
    'googleads',
    'googletagmanager.com',
    'google'
] as const;

const RETRY_QUERY = '_rr';

/** 已做过一次自愈重试的资源 URL（归一化后） */
const retriedUrls = new Set<string>();

let guardInstalled = false;

export const resetResourceLoadGuardForTest = () => {
    retriedUrls.clear();
    guardInstalled = false;
};

const isResourceTarget = (
    target: EventTarget | null
): target is HTMLImageElement | HTMLScriptElement | HTMLLinkElement => {
    if (!target || !(target instanceof Element)) {
        return false;
    }

    const tag = target.tagName;

    if (tag === 'IMG' || tag === 'SCRIPT') {
        return true;
    }

    if (tag === 'LINK') {
        const rel = (target as HTMLLinkElement).rel || '';

        return rel.split(/\s+/).some((item) => item.toLowerCase() === 'stylesheet');
    }

    return false;
};

const getResourceUrl = (
    target: HTMLImageElement | HTMLScriptElement | HTMLLinkElement
): string => {
    if (target instanceof HTMLImageElement) {
        return target.currentSrc || target.src || '';
    }

    if (target instanceof HTMLScriptElement) {
        return target.src || '';
    }

    return target.href || '';
};

/** 去掉自愈参数，便于同一资源去重 */
export const normalizeResourceUrl = (url: string): string => {
    if (!url) {
        return '';
    }

    try {
        const parsed = new URL(url, window.location.href);

        parsed.searchParams.delete(RETRY_QUERY);

        return parsed.toString();
    } catch {
        return url.replace(
            new RegExp(`([?&])${RETRY_QUERY}=\\d+&?`),
            '$1'
        ).replace(/[?&]$/, '');
    }
};

export const isSilentThirdPartyScript = (url: string): boolean => {
    if (!url) {
        return false;
    }

    const lower = url.toLowerCase();

    return SILENT_SCRIPT_PATTERNS.some((pattern) => lower.includes(pattern));
};

const withRetryQuery = (url: string): string => {
    try {
        const parsed = new URL(url, window.location.href);

        parsed.searchParams.set(RETRY_QUERY, '1');

        return parsed.toString();
    } catch {
        const joiner = url.includes('?') ? '&' : '?';

        return `${url}${joiner}${RETRY_QUERY}=1`;
    }
};

const retryImage = (img: HTMLImageElement, url: string) => {
    img.src = withRetryQuery(url);
};

const retryScript = (script: HTMLScriptElement, url: string) => {
    const next = document.createElement('script');

    Array.from(script.attributes).forEach((attr) => {
        if (attr.name === 'src') {
            return;
        }

        next.setAttribute(attr.name, attr.value);
    });
    // boolean 属性以 IDL 为准，避免 jsdom / 空字符串丢失
    next.async = script.async;
    next.defer = script.defer;
    next.src = withRetryQuery(url);
    (script.parentNode || document.head || document.documentElement).appendChild(
        next
    );
};

const retryStylesheet = (link: HTMLLinkElement, url: string) => {
    const nextHref = withRetryQuery(url);

    // 先改 href 触发浏览器重新请求；部分环境需克隆节点更稳
    link.href = nextHref;
};

const attemptHeal = (
    target: HTMLImageElement | HTMLScriptElement | HTMLLinkElement,
    url: string
): boolean => {
    const key = normalizeResourceUrl(url);

    if (!key || retriedUrls.has(key)) {
        return false;
    }

    retriedUrls.add(key);

    try {
        if (target instanceof HTMLImageElement) {
            retryImage(target, url);

            return true;
        }

        if (target instanceof HTMLScriptElement) {
            retryScript(target, url);

            return true;
        }

        retryStylesheet(target, url);

        return true;
    } catch (error) {
        console.warn('[resourceLoadGuard] retry failed:', error);

        return false;
    }
};

export type ResourceErrorHandleResult = {
    /** 是否应吞掉本次 error（不向后续监听器 / 主监控传播） */
    swallow: boolean;
    reason?: 'not-resource' | 'silent' | 'healed' | 'final-failure';
};

/**
 * 处理资源加载错误。
 * - 非资源节点：不处理
 * - 可静默第三方 script：吞掉
 * - 首次失败：自愈重试并吞掉
 * - 重试后仍失败：放行上报
 */
export const handleResourceLoadError = (
    event: ErrorEvent
): ResourceErrorHandleResult => {
    const { target } = event;

    if (!isResourceTarget(target)) {
        return { swallow: false, reason: 'not-resource' };
    }

    const url = getResourceUrl(target);

    if (target instanceof HTMLScriptElement && isSilentThirdPartyScript(url)) {
        return { swallow: true, reason: 'silent' };
    }

    if (attemptHeal(target, url)) {
        return { swallow: true, reason: 'healed' };
    }

    return { swallow: false, reason: 'final-failure' };
};

/**
 * 注册捕获阶段资源错误治理（幂等）。
 * 在 capture 阶段拦截，避免首次瞬时失败进入主监控。
 */
export const installResourceLoadGuard = () => {
    if (guardInstalled || typeof window === 'undefined') {
        return;
    }

    guardInstalled = true;

    window.addEventListener(
        'error',
        (event) => {
            const result = handleResourceLoadError(event as ErrorEvent);

            if (!result.swallow) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
        },
        true
    );
};
